import { type PayloadAction, createSlice } from '@reduxjs/toolkit';


import { type Group } from '@/group/domain/group';
import { type Invitation } from '@/group/domain/invitation';
import { type Lounge, LoungeState, type Parking, type PlaceType } from '@/parking/domain/parking';

export interface ParkingSlice {
    pendingParking: Parking
    parking: Parking
    parkingAdminGroup: Group
}

export const initialPendingParkingState: Parking = {
    address: {
        address: '',
        city: '',
        country: '',
        zip: ''
    },
    commercialInfos: {
        VATlegislation: '',
        companyNumber: ''
    },
    contact: {
        email: '',
        fax: '',
        firstname: '',
        phone: '',
        surname: ''
    },
    email: '',
    lounges: [],
    placeTypes: [],
    name: '',
    operations: {
        GMThour: 0,
        chargingPointsNumber: 0,
        defaultLanguage: 'fin',
        placesCapacity: 0,
        plateNumberReader: false,
        pinCodeRequired: false
    },
    phone: '',
};

const initialState: ParkingSlice = {
    parking: {
        address: {
            address: '',
            city: '',
            country: '',
            zip: ''
        },
        commercialInfos: {
            VATlegislation: '',
            companyNumber: ''
        },
        contact: {
            email: '',
            fax: '',
            firstname: '',
            phone: '',
            surname: ''
        },
        email: '',
        placeTypes: [],
        lounges: [],
        name: '',
        operations: {
            GMThour: 0,
            chargingPointsNumber: 0,
            defaultLanguage: 'fin',
            placesCapacity: 0,
            plateNumberReader: false,
            pinCodeRequired: false
        },
        phone: '',
    },
    parkingAdminGroup: {
        assignedFleets: [],
        defaultDuration: 0,
        groupConfigs: [],
        invitations: [],
        members: [],
        name: '',
        prefix: ''
    },
    pendingParking: initialPendingParkingState
};


const parkingSlice = createSlice({
    initialState,
    name: 'parking',
    reducers: {
        clearParking(state) {
            state.parking = initialState.parking;
        },
        clearParkingAdminGroup(state) {
            state.parkingAdminGroup = initialState.parkingAdminGroup;
        },
        clearPendingParking(state) {
            state.pendingParking = initialState.pendingParking;
        },
        removeInvitationFromParkingAdminGroup(state, action: PayloadAction<number>) {
            const invitations = (state.parkingAdminGroup.invitations as Invitation[])?.slice();
            invitations?.splice(action.payload, 1); // TODO replace by find with id?
            state.parkingAdminGroup.invitations = invitations;
            return state;
        },
        setParking(state, action: PayloadAction<Parking>) {
            state.parking = action.payload;
            return state;
        },
        setParkingAdminGroup(state, action: PayloadAction<Group>) {
            state.parkingAdminGroup = action.payload;
            return state;
        },
        setPendingParking(state, action: PayloadAction<Parking>) {
            state.pendingParking = action.payload;
            return state;
        },
        upsertLoungeField(state, action: PayloadAction<Lounge>) {
            let bufferarray: Lounge[] = [];
            const lgIndex = state.pendingParking.lounges?.findIndex(item => item.id === action.payload.id);
            if (lgIndex > -1) {
                bufferarray = [...state.pendingParking.lounges];
                bufferarray.splice(lgIndex, 1, action.payload);
                
            } else {
                const topId = state.pendingParking.lounges?.reduce<unknown>((acc, item) => { 
                    return (item as Lounge)?.id ?? 0 > (acc as number) ? item.id : acc;
                }, 0) as number;
                bufferarray = [...state.pendingParking.lounges, { ...action.payload, id: 'new_' + String(topId + 1), refAutomatisme: '', state: LoungeState.Available, accessibility: Boolean(action.payload.accessibility)}];
            }
            state.pendingParking = { ...state.pendingParking, lounges: bufferarray};
            
            return state;
        },
        removeLoungeField(state, action: PayloadAction<number>) {
            let bufferarray: Lounge[] = [];
            bufferarray = [...state.pendingParking.lounges];
            if (action.payload > -1) {
                bufferarray.splice(action.payload, 1);
            } 
            state.pendingParking = { ...state.pendingParking, lounges: bufferarray};
            
            return state;
        },
        upsertPlaceTypeField(state, action: PayloadAction<PlaceType>) {
            let bufferarray: PlaceType[] = [];
            const lgIndex = state.pendingParking.placeTypes?.findIndex(item => item.id === action.payload.id);
            if (lgIndex > -1) {
                bufferarray = [...state.pendingParking.placeTypes];
                bufferarray.splice(lgIndex, 1, action.payload);
                
            } else {
                const botId = state.pendingParking.placeTypes?.reduce<unknown>((acc, item) => { 
                    return (item as PlaceType)?.id ?? 0 < (acc as number) ? item.id : acc;
                }, 0) as number;
                bufferarray = [...state.pendingParking.placeTypes, { ...action.payload, id: (Math.min(botId, 0) - 1)}];
            }
            state.pendingParking = { ...state.pendingParking, placeTypes: bufferarray};
            
            return state;
        },
        removePlaceTypeField(state, action: PayloadAction<number>) {
            let bufferarray: PlaceType[] = [];
            bufferarray = [...state.pendingParking.placeTypes];
            if (action.payload > -1) {
                bufferarray.splice(action.payload, 1);
            } 
            state.pendingParking = { ...state.pendingParking, placeTypes: bufferarray};
            
            return state;
        },
        updateParkingField(state, action: PayloadAction<{ field: string, value: string }>) {
            const { field, value } = action.payload;

            // Check if parking exists
            if (!state.pendingParking) {
                return state; // Return current state if pendingParking doesn't exist
            }

            // Split field into nested parts
            const fieldParts = field.split('.');

            // If field is nested
            if (fieldParts.length > 1) {
                const topLevelField = fieldParts[0];
                const nestedFields = fieldParts.slice(1); // Remove the first part (top-level field)

                // Check if top-level field exists
                if (!state.pendingParking[topLevelField]) {
                    return state; // Return current state if top-level field doesn't exist
                }

                // Create a new object with updated nested fields
                const updatedNestedFields = {
                    ...state.pendingParking[topLevelField],
                    [nestedFields.join('.')]: value // Update the nested field
                };

                // Create a new pendingParking object with the updated nested fields
                const updatedParking = {
                    ...state.pendingParking,
                    [topLevelField]: updatedNestedFields // Update the top-level field
                };

                // Return the new state with updated pendingParking
                state.pendingParking = updatedParking;

                return state;
            } else {

                // If field is not nested, update it directly
                state.pendingParking = {
                    ...state.pendingParking,
                    [field]: value // Update the field
                };

                return state;
            }
        },
    }

});

export const { setPendingParking, clearParking, upsertPlaceTypeField, removePlaceTypeField, removeLoungeField, upsertLoungeField, removeInvitationFromParkingAdminGroup, clearParkingAdminGroup, setParkingAdminGroup, clearPendingParking, updateParkingField, setParking } = parkingSlice.actions;
export default parkingSlice.reducer;
